import React, { createContext, useState, useContext, useMemo, useEffect } from 'react';
import { useAppContext } from '../AppContext';

const LocationFilterContext = createContext();

export const useLocationFilter = () => useContext(LocationFilterContext);

export const LocationFilterProvider = ({ children }) => {
  const { currentUserProfile } = useAppContext();

  // --- Unified State Management ---
  const [filterType, setFilterTypeState] = useState(() => localStorage.getItem('discovery_filterType') || 'global');
  const [locationFilterRadius, setLocationFilterRadiusState] = useState(() => Number(localStorage.getItem('discovery_radius')) || 50);
  const [languagePreference, setLanguagePreferenceState] = useState(() => (localStorage.getItem('discovery_languagePref') === 'true') || false);
  
  const [userLocation, setUserLocationState] = useState(null);
  const [isLocationLoading, setIsLocationLoading] = useState(true);
  const [isProfileSyncComplete, setIsProfileSyncComplete] = useState(false);

  // --- Sync with Firestore Profile on Load ---
  useEffect(() => {
    if (currentUserProfile) { // Check if the profile object has loaded
      if (currentUserProfile.discoverySettings) {
        const { filterType: profileFilter, radius: profileRadius, languagePref: profileLangPref } = currentUserProfile.discoverySettings;
        
        setFilterTypeState(profileFilter || 'global');
        setLocationFilterRadiusState(profileRadius || 50);
        setLanguagePreferenceState(profileLangPref || false);

        // Also update localStorage to keep it in sync
        localStorage.setItem('discovery_filterType', profileFilter || 'global');
        localStorage.setItem('discovery_radius', profileRadius || 50);
        localStorage.setItem('discovery_languagePref', profileLangPref || false);
      }
      // Mark sync as complete regardless of whether settings were found or not.
      // This allows the app to proceed with either saved settings or defaults.
      setIsProfileSyncComplete(true);
    }
  }, [currentUserProfile]);

  // --- Location Fallback Logic ---
  useEffect(() => {
    // If we already have a location (e.g., from a manual set or previous session), we're not loading.
    if (userLocation) {
      setIsLocationLoading(false);
      return;
    }

    // If we don't have a location yet, but the user's profile has loaded,
    // we can check it for a fallback location.
    if (currentUserProfile) {
      if (currentUserProfile.location) {
        setUserLocationState(currentUserProfile.location);
      }
      // Whether the profile had a location or not, we are done checking, so loading is finished.
      setIsLocationLoading(false);
    }
    // Note: If currentUserProfile is not yet available, isLocationLoading remains true from its initial state, which is correct.
  }, [currentUserProfile, userLocation]);

  // --- State Persistence ---
  const setFilterType = (type) => {
    localStorage.setItem('discovery_filterType', type);
    setFilterTypeState(type);
  };

  const setLocationFilterRadius = (radius) => {
    localStorage.setItem('discovery_radius', radius);
    setLocationFilterRadiusState(radius);
  };

  const setLanguagePreference = (isEnabled) => {
    localStorage.setItem('discovery_languagePref', isEnabled);
    setLanguagePreferenceState(isEnabled);
  };

  const setUserLocation = (location) => {
    setUserLocationState(location);
  };

  const value = useMemo(() => ({
    filterType,
    locationFilterRadius,
    languagePreference,
    userLocation,
    isLocationLoading,
    isProfileSyncComplete, // <-- Add this
    setFilterType,
    setLocationFilterRadius,
    setLanguagePreference,
    setUserLocation,
  }), [filterType, locationFilterRadius, languagePreference, userLocation, isLocationLoading, isProfileSyncComplete]); // <-- And add here

  return (
    <LocationFilterContext.Provider value={value}>
      {children}
    </LocationFilterContext.Provider>
  );
};
